import { ActionIcon, Alert, Box, Button, Group, MultiSelect, Stack, Switch, Tabs, TextInput, ThemeIcon } from "@mantine/core";
import { IconExclamationCircle, IconInfoTriangle, IconPlus, IconPolygon, IconTrash } from "@tabler/icons-react";
import bbox from "@turf/bbox";
import * as turf from "@turf/turf";
import { useEffect, useState } from "react";
import { MapProvider } from "react-map-gl/maplibre";
import { Section, SectionProvider } from "shared/components/global/Section";
import { useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { IpawsAlertFormStepLayout } from "shared/components/ipaws/alert/IpawsAlertFormStepLayout";
import { useIpawsAlertFormAllowedGeoCodes } from "shared/components/ipaws/alert/query";
import { useIpawsAlertActions } from "shared/components/ipaws/alert/useIpawsAlertActions";
import { MapBase } from "shared/components/map/MapBase";
import { MapControlledBoundsOverlay } from "shared/components/map/MapControlledBoundsOverlay";
import { MapFeatureOverlay } from "shared/components/map/MapFeatureOverlay";
import { MapPolygonDrawOverlay } from "shared/components/map/MapPolygonDrawOverlay";
import { MapSearch } from "shared/components/map/MapSearch";
import { useFormState } from "shared/hooks/useFormState";
import { arr } from "shared/utils/array";
import { fail, runAsync, to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { zx } from "shared/utils/zx";
import { IpawsChannel } from "src/gql/graphql";

export const IpawsAlertFormArea = () => {
    const form = useIpawsAlertFormContext();
    const [areas, setAreas] = useFormState(form, "areas");
    const [certId] = useFormState(form, "certId");
    const [activeAreaIdx, setActiveAreaIdx] = useState(0);

    const [channels] = useFormState(form, "channels");
    const [selectedGeocodes] = useFormState(form, `areas.${activeAreaIdx}.codes`);
    const { data: geoCodes } = useIpawsAlertFormAllowedGeoCodes();

    const [showFipsCodesOnMap, setShowFipsCodesOnMap] = useState(false);

    const authorizedArea = turf.union(turf.featureCollection(geoCodes?.map((gc) => turf.feature(gc.boundary.geometry)) ?? []));
    const drawnPolygonsFallOutsideFipsBoundary = areas[activeAreaIdx].features.some(
        (f) => !!authorizedArea && !turf.booleanContains(authorizedArea, turf.polygon(f.geometry.coordinates)),
    );

    const { validate } = useIpawsAlertActions();
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        setValidated(false);
    }, [JSON.stringify(areas[activeAreaIdx])]);

    useEffect(() => {
        if (!!form.getValues().areas[activeAreaIdx].meta.bounds) return;
        form.setFieldValue(
            `areas.${activeAreaIdx}.meta.bounds`,
            zx.lngLatBounds.coerce.fromBBox(
                bbox({
                    type: "FeatureCollection",
                    features: geoCodes?.map(to("boundary")) ?? [],
                }),
            ),
        );
    }, [activeAreaIdx]);

    return (
        <IpawsAlertFormStepLayout>
            <SectionProvider>
                <Tabs
                    value={activeAreaIdx.toString()}
                    onChange={(next) => {
                        if (!next || next.startsWith("$$")) return;
                        setActiveAreaIdx(Number.parseInt(next));
                    }}
                >
                    <Tabs.List>
                        {areas.map((area, i) => (
                            <Tabs.Tab
                                leftSection={
                                    !!Object.keys(form.errors).some((k) => k.startsWith(`areas.${i}`)) && (
                                        <ThemeIcon variant="transparent" c="var(--mantine-color-error)">
                                            <IconExclamationCircle size={16} />
                                        </ThemeIcon>
                                    )
                                }
                                key={i}
                                value={i.toString()}
                                rightSection={
                                    areas.length > 1 && (
                                        <ActionIcon
                                            variant="transparent"
                                            size="xs"
                                            onClick={(evt) => {
                                                evt.stopPropagation();
                                                void notify.confirm(`Are you sure you want to remove "${area.name}"?`).then(({ close }) => {
                                                    if (activeAreaIdx == i) setActiveAreaIdx(0); // if we are deleting the active tab, switch to the first tab
                                                    setAreas(arr(areas).eqBy("name").except(area).take());
                                                    close();
                                                });
                                            }}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    )
                                }
                            >
                                {area.name || "[Untitled Area]"}
                            </Tabs.Tab>
                        ))}
                        <Tabs.Tab
                            value="$$new"
                            leftSection={<IconPlus size={16} />}
                            onClick={() => {
                                setAreas(
                                    areas.concat([
                                        { name: "Area " + (areas.length + 1), circles: [], codes: [], features: [], meta: { search: "" } },
                                    ]),
                                );
                            }}
                        >
                            Add Area
                        </Tabs.Tab>
                    </Tabs.List>
                </Tabs>
                <Stack>
                    <TextInput label="Area Name" required {...form.getInputProps(`areas.${activeAreaIdx}.name`)} />
                    <MultiSelect
                        label="FIPS Codes"
                        required
                        data={geoCodes?.map(to("code"))}
                        {...form.getInputProps(`areas.${activeAreaIdx}.codes`)}
                    />

                    {channels.includes(IpawsChannel.Cmas) && (
                        <>
                            <Group justify="flex-end">
                                <Switch
                                    label="Show on Map"
                                    checked={showFipsCodesOnMap}
                                    onChange={(e) => setShowFipsCodesOnMap(e.target.checked)}
                                />
                            </Group>
                            <Section label="Map">
                                <MapProvider>
                                    <Stack>
                                        <Group>
                                            <Box flex={1}>
                                                <MapSearch
                                                    value={areas.at(activeAreaIdx)?.meta.search}
                                                    onChange={(next) => form.setFieldValue(`areas.${activeAreaIdx}.meta.search`, next)}
                                                    mapId="ipaws-alert-create-map"
                                                />
                                            </Box>
                                            <Button
                                                variant="default"
                                                leftSection={<IconPolygon size={16} />}
                                                disabled={!areas.at(activeAreaIdx)?.features.length}
                                                onClick={() =>
                                                    form.setFieldValue(
                                                        `areas.${activeAreaIdx}.meta.bounds`,
                                                        zx.lngLatBounds.coerce.fromBBox(
                                                            bbox({
                                                                type: "FeatureCollection",
                                                                features: areas.at(activeAreaIdx)?.features ?? [],
                                                            }),
                                                        ),
                                                    )
                                                }
                                            >
                                                Fit Shapes
                                            </Button>
                                        </Group>
                                        <Box pos="relative" h="65dvh" w="100%" style={{ overflow: "clip" }}>
                                            <MapBase id="ipaws-alert-create-map">
                                                {showFipsCodesOnMap && (
                                                    <MapFeatureOverlay
                                                        noFit
                                                        features={
                                                            geoCodes?.map((it) => ({
                                                                ...it.boundary,
                                                                properties: {
                                                                    fill: selectedGeocodes.includes(it.code) ? "gray" : "transparent",
                                                                },
                                                            })) ?? []
                                                        }
                                                        stroke="gray"
                                                    />
                                                )}
                                                <MapControlledBoundsOverlay
                                                    bounds={areas.at(activeAreaIdx)?.meta.bounds}
                                                    onBoundsChange={(next) =>
                                                        form.setFieldValue(
                                                            `areas.${activeAreaIdx}.meta.bounds`,
                                                            zx.lngLatBounds().parse(next.toArray()),
                                                        )
                                                    }
                                                />
                                                <MapPolygonDrawOverlay
                                                    position="top-left"
                                                    features={areas.at(activeAreaIdx)?.features ?? []}
                                                    onChange={(fs) => form.setFieldValue(`areas.${activeAreaIdx}.features`, fs)}
                                                />
                                            </MapBase>
                                        </Box>
                                    </Stack>
                                </MapProvider>

                                {drawnPolygonsFallOutsideFipsBoundary && !validated && (
                                    <Alert icon={<IconInfoTriangle />} title="Warning">
                                        <Group justify="space-between">
                                            The area you drew is outside the certificate jurisdiction. This may cause the alert to fail.
                                            <Button
                                                variant="outline"
                                                loading={validate.isPending}
                                                onClick={() =>
                                                    runAsync(async () => {
                                                        if (!certId) return fail("Expected certId to be set.");
                                                        const { result } = await validate.mutateAsync({
                                                            certId,
                                                            polygons: areas[activeAreaIdx].features
                                                                .map(to("geometry"))
                                                                .map((it) => JSON.stringify(it)),
                                                        });

                                                        if (result.success) {
                                                            setValidated(true);
                                                            return;
                                                        }

                                                        notify.show.warn({
                                                            message: "Validation failed. Your drawn region will be rejected by IPAWS.",
                                                        });
                                                    })
                                                }
                                            >
                                                Validate
                                            </Button>
                                        </Group>
                                    </Alert>
                                )}
                            </Section>
                        </>
                    )}
                </Stack>
            </SectionProvider>
        </IpawsAlertFormStepLayout>
    );
};
